import { DataRow, Result, RootApiFilter, RootApiSort, UploadType } from "@/api/types";
import axios from "axios";
import { generatePresignedUrl, uploadToS3 } from "./utils";

interface ValidateColumnsProps {
  fileName: string;
  dataType: string;
  location?: string;
}

type ValidateColumnsResult = {
  result: string;
  status: "SUCCESS" | "FAILED";
  err?: {
    invalid_columns: string;
    missing_columns: string;
    error: string;
  };
};

export const validateColumns = async ({
  fileName,
  dataType,
  location,
}: ValidateColumnsProps): Promise<ValidateColumnsResult> => {
  try {
    const { data } = await axios.get(
      `/data/validate_columns?data_year=2022&file_name=${fileName}&upload_type=${dataType}&location=${
        location || ""
      }`
    );
    return data;
  } catch (e: any) {
    return e.response?.data?.err && e.response.data;
  }
};

interface CreateUploadRequestProps {
  productId?: string;
  uploadType: UploadType;
  uploadData: boolean;
  fileName?: string;
  body?: any;
  UUID?: string;
  year?: number;
  callback?: () => void;
  facilityId?: number;
}

interface CreateUploadRequestResult extends Result {
  result: {
    UUID: string;
  };
}

export const createUploadRequest = async ({
  fileName,
  productId,
  uploadType,
  uploadData,
  body,
  UUID,
  year,
  facilityId,
}: CreateUploadRequestProps) => {
  let params = {
    data_year: 2022,
    upload_type: uploadType,
    uploading_for: productId || facilityId || year || "",
    upload_data: uploadData,
    ...(year ? { year } : {}),
  };
  if (fileName) {
    return axios.post<CreateUploadRequestResult>(`/data/create_upload_request`, undefined, {
      params: {
        ...params,
        file_name: fileName,
      },
    });
  } else if (body) {
    const p = await generatePresignedUrl({ file_name: "y.json" });

    await uploadToS3({ url: p.url, file: body });
    return axios.post<CreateUploadRequestResult>(`/data/create_upload_request`, undefined, {
      params: {
        ...params,
        file_name: p.key,
      },
    });
  } else if (UUID) {
    return axios.post<CreateUploadRequestResult>(`/data/create_upload_request`, undefined, {
      params: {
        ...params,
        UUID,
      },
    });
  }

  throw Error("Create upload request called without file UUID or anything");
};

interface MetaData {
  orders_per_year?: Record<any, any>;
  total_new_facilities?: number;
  total_facilities_updated?: number;
  business_travel_per_year?: Record<any, any>;
  commute_per_year?: Record<any, any>;
  transport_per_year?: Record<any, any>;
}

export type UploadStatus =
  | "pending"
  | "loading_data"
  | "validating_data"
  | "uploading_validation_result"
  | "importing_data"
  | "failed"
  | "success";

interface GetUploadStatus extends Result {
  result: {
    progress: number;
    meta_data: MetaData;
    status: UploadStatus;
  };
}

export const getUploadStatus = async ({ UUID }: { UUID: string }) => {
  return axios.get<GetUploadStatus>(`/data/get_upload_status?UUID=${UUID}`);
};

interface PaginateUploadResultProps {
  UUID: string;
  page?: number;
  pageSize?: number;
  activeFilters?: RootApiFilter;
  activeSort?: RootApiSort;
}

interface PaginateUploadResult extends Result {
  total_errors: number;
  total_correct: number;
  total_notes: number;
  total_ignored: number;
  total_rows: number;
  result: DataRow[];
  filters: RootApiFilter;
  data_changed: boolean;
}

export const readData = async ({
  UUID,
  page,
  pageSize,
  activeFilters,
  activeSort,
}: PaginateUploadResultProps) => {
  const { data } = await axios.post<PaginateUploadResult>(
    `/data/read_data?UUID=${UUID}&page=${page || 1}&rows_per_page=${pageSize || 50}`,
    {
      filters: activeFilters,
      sorter: activeSort,
    }
  );

  return data;
};

export const deleteData = async ({
  uploadType,
  locationName,
  productId,
  from,
  to,
  byTransportCategory: by_transport_category,
}: {
  uploadType: string;
  locationName?: string;
  productId?: string;
  from?: string;
  to?: string;
  byTransportCategory?: boolean;
}) => {
  const { data } = await axios.delete(`/data/clear_data`, {
    params: {
      data_year: 2022,
      upload_type: uploadType,
      location: locationName,
      product_id: productId,
      from: from,
      to: to,
      by_transport_category: by_transport_category,
    },
  });

  return data.result;
};
